import { Box, FormHelperText, Grid } from "@mui/material";
import { Formik, Form, Field } from "formik";
import useLocationHelper from "./helper";
import ContainedButton from "../../components/contained-button";
import { BackArrow } from "../../utils/common/constants";
import Map from "./map";
import "./selectLocation.scss";
import "../../layout/loginWrapper/authContainer.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "src/hooks/useAppSelector";
import { LocationData, LocationSchema } from "./location.validator";
import { useRef } from "react";
import { showAlert } from "src/utils";


function LocationDetails() {
  const mapLocationRef = useRef({ lat: 0, lng: 0 }); // Initial location (San Francisco)

  const { addAddressFromPayload } = useLocationHelper();
  const navigate = useNavigate();
  const locationNav = useLocation();

  /**
   * Retrieves the user's location details from the application state.
   *
   * This code is used to find the location object in the user's details that matches the current location navigation state. If a matching location is found, its data is returned as the initial values for a form.
   *
   * @returns {any} The location details object, or undefined if not found.
   */
  let initialValues: any = useAppSelector((state) => {
    try {
      return state.userInfo.userDetail.locations.find(
        (loc) => loc.id === locationNav.state.id
      );
    } catch (error: any) {
      showAlert(2, error.message);
    }
  });

  if (locationNav?.state?.location?.adoption_code) {
    initialValues = { ...locationNav?.state?.location?.location };
  }
  mapLocationRef.current = {
    lat: initialValues?.coordinates?.y,
    lng: initialValues?.coordinates?.x,
  };

 
 /**
   * Handles the submission of the location details form.
   *
   * This function is called when the user submits the location details form. It takes the form payload, which contains the updated location details, and performs the following actions:
   *
   * 1. Converts the `phone` field to a string if it is not already a string.
   * 2. Updates the `coordinates` field with the current map location.
   * 3. Calls the `addAddressFromPayload` function from the `useLocationHelper` hook to save the updated location details.
   *
   * @param {LocationData} payload - The updated location details.
   */
  const handleOnSubmit = (payload: LocationData) => {
    payload.phone = payload.phone ? payload.phone.toString() : "";
    payload.coordinates =
      mapLocationRef?.current?.lng + "," + mapLocationRef?.current?.lat;
    addAddressFromPayload(payload);
  };

  const validLocationData: LocationData = {
    id: initialValues?.id,
    display_name: initialValues?.name,
    address: initialValues?.address,
    city: initialValues?.city,
    region: initialValues?.region,
    postal_code: initialValues?.postal_code,
    phone: initialValues?.phone,
    description: initialValues?.description ?? "",
    coordinates: mapLocationRef?.current?.lng + "," + mapLocationRef?.current?.lat,
  };

  return (
    <Box className="onboarding_wrapper">
      <Box className="lt_map_panel">
        <Map mapLocationRef={mapLocationRef} locationAddress={initialValues} adoption_code={locationNav?.state?.location?.adoption_code} />
      </Box>

      <Box className="rt_auth_panel location_rt" sx={{ mt: -5 }}>
        <Box className="auth_internal_container location_container">
          <Box onClick={() => navigate(-1)} sx={{ mb: 3 }}>
            <img src={BackArrow} alt="" />
          </Box>
          <Formik
            initialValues={validLocationData}
            validationSchema={LocationSchema}
            validateOnChange
            onSubmit={handleOnSubmit}
            key={JSON.stringify(initialValues)}
          >
            {({ errors, touched }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box className="inp_formCtrl">
                      <label>Location Name</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M19.0476 0.951285C18.1401 0.0429518 16.8285 -0.239548 15.601 0.222952L1.93931 5.88462C0.325978 6.49212 -0.172355 7.99795 0.050145 9.21962C0.273478 10.443 1.27015 11.6763 2.99514 11.6763H8.31098V17.0046C8.31098 18.7288 9.54431 19.7263 10.7668 19.948C10.9493 19.9813 11.1376 19.9988 11.3285 19.9988C12.4193 19.9988 13.5851 19.4371 14.0943 18.0863L19.786 4.37212C20.2393 3.17045 19.9568 1.85879 19.0476 0.951285ZM18.236 3.75879L12.5443 17.473C12.256 18.2355 11.5901 18.4046 11.066 18.3096C10.5393 18.2138 9.97681 17.8205 9.97681 17.0055V10.8438C9.97681 10.383 9.60431 10.0105 9.14348 10.0105H2.99514C2.17931 10.0105 1.78598 9.44795 1.69015 8.92129C1.59514 8.39545 1.76431 7.73045 2.55264 7.43379L16.2143 1.77212C16.4026 1.70129 16.5935 1.66629 16.781 1.66629C17.1818 1.66629 17.566 1.82545 17.8701 2.12962C18.316 2.57545 18.4485 3.19462 18.236 3.75879Z" />
                          </svg>
                        </i>
                        <Field
                          type="text"
                          id="display_name"
                          placeholder="Enter location name"
                          name="display_name"
                        />
                      </Box>
                      {touched.display_name && errors.display_name && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-email-login"
                        >
                          {errors.display_name}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="inp_formCtrl">
                      <label>Phone Number</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="16"
                            height="22"
                            viewBox="0 0 16 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M10.7493 22H5.24935C2.7221 22 0.666016 19.9439 0.666016 17.4167V4.58333C0.666016 2.05608 2.7221 0 5.24935 0H10.7493C13.2766 0 15.3327 2.05608 15.3327 4.58333V17.4167C15.3327 19.9439 13.2766 22 10.7493 22ZM5.24935 1.83333C3.73318 1.83333 2.49935 3.06717 2.49935 4.58333V17.4167C2.49935 18.9328 3.73318 20.1667 5.24935 20.1667H10.7493C12.2655 20.1667 13.4993 18.9328 13.4993 17.4167V4.58333C13.4993 3.06717 12.2655 1.83333 10.7493 1.83333H5.24935ZM9.83268 17.4167C9.83268 16.9107 9.42202 16.5 8.91602 16.5H7.08268C6.57668 16.5 6.16602 16.9107 6.16602 17.4167C6.16602 17.9227 6.57668 18.3333 7.08268 18.3333H8.91602C9.42202 18.3333 9.83268 17.9227 9.83268 17.4167Z" />
                          </svg>
                        </i>{" "}
                        <span className="num">+1</span>
                        <Field
                          id="phone"
                          type="number"
                          placeholder="Enter Phone Number"
                          name="phone"
                        />
                      </Box>
                      {errors.phone && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-email-login"
                        >
                          {errors.phone}
                        </FormHelperText>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 4 }}>
                    <div className="formHd">LOCATION DETAILS</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="inp_formCtrl">
                      <label>Street Address</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M4.16996 20.0042C3.71329 20.0042 3.25579 19.8392 2.89996 19.5075L1.24163 17.9667C-0.405039 16.3208 -0.405039 13.6775 1.21996 12.0533C2.00746 11.2667 3.05413 10.8333 4.16663 10.8333C5.27913 10.8333 6.3258 11.2667 7.11246 12.0533C7.89996 12.84 8.33329 13.8867 8.33329 15C8.33329 16.1133 7.89996 17.1592 7.11246 17.9458L5.44496 19.505C5.08829 19.8383 4.62996 20.005 4.17163 20.005L4.16996 20.0042ZM4.16579 12.4992C3.49829 12.4992 2.86996 12.7592 2.39746 13.2317C1.42329 14.2058 1.42329 15.7917 2.39746 16.7667L4.03413 18.2867C4.11079 18.3575 4.22913 18.3575 4.30579 18.2867L5.95329 16.7475C6.40496 16.295 6.66496 15.6675 6.66496 14.9992C6.66496 14.3308 6.40496 13.7033 5.93246 13.2308C5.46079 12.7592 4.83329 12.4992 4.16579 12.4992ZM15.8366 9.17083C15.38 9.17083 14.9225 9.005 14.5666 8.67417L12.9083 7.13333C11.2616 5.4875 11.2616 2.84417 12.8866 1.22C13.6733 0.433333 14.72 0 15.8333 0C16.9466 0 17.9916 0.433333 18.7791 1.22C20.4041 2.845 20.4041 5.48833 18.7791 7.1125L17.1116 8.67167C16.755 9.005 16.2958 9.17167 15.8375 9.17167L15.8366 9.17083ZM15.8325 1.66583C15.1641 1.66583 14.5366 1.92583 14.0641 2.3975C13.09 3.3725 13.09 4.95833 14.0641 5.93333L15.7 7.45333C15.7775 7.525 15.8966 7.52333 15.9725 7.45333L17.62 5.91417C18.5741 4.95917 18.5741 3.37333 17.6 2.39833C17.1275 1.92583 16.5 1.66583 15.8325 1.66583ZM19.9991 16.6658C19.9991 14.8275 18.5041 13.3325 16.6658 13.3325H12.4991C11.58 13.3325 10.8325 12.585 10.8325 11.6658C10.8325 10.96 11.2791 10.3292 11.9433 10.0942C12.3775 9.94083 12.605 9.465 12.4516 9.03083C12.2983 8.59667 11.8233 8.36833 11.3883 8.5225C10.0591 8.99167 9.16579 10.2558 9.16579 11.6658C9.16579 13.5042 10.6608 14.9992 12.4991 14.9992H16.6658C17.585 14.9992 18.3325 15.7467 18.3325 16.6658C18.3325 17.585 17.585 18.3325 16.6658 18.3325H9.16579C8.7058 18.3325 8.33246 18.7058 8.33246 19.1658C8.33246 19.6258 8.7058 19.9992 9.16579 19.9992H16.6658C18.5041 19.9992 19.9991 18.5042 19.9991 16.6658Z" />
                          </svg>
                        </i>
                        <Field
                          id="address"
                          placeholder="Enter Street Address"
                          name="address"
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className="inp_formCtrl">
                      <label>City</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M15.8333 12.5H15C14.5392 12.5 14.1667 12.1275 14.1667 11.6667C14.1667 11.2058 14.5392 10.8333 15 10.8333H15.8333C16.2942 10.8333 16.6667 11.2058 16.6667 11.6667C16.6667 12.1275 16.2942 12.5 15.8333 12.5ZM16.6667 15C16.6667 14.5392 16.2942 14.1667 15.8333 14.1667H15C14.5392 14.1667 14.1667 14.5392 14.1667 15C14.1667 15.4608 14.5392 15.8333 15 15.8333H15.8333C16.2942 15.8333 16.6667 15.4608 16.6667 15ZM13.3333 5C13.3333 4.53917 12.9608 4.16667 12.5 4.16667H11.6667C11.2058 4.16667 10.8333 4.53917 10.8333 5C10.8333 5.46083 11.2058 5.83333 11.6667 5.83333H12.5C12.9608 5.83333 13.3333 5.46083 13.3333 5ZM16.6667 5C16.6667 4.53917 16.2942 4.16667 15.8333 4.16667H15C14.5392 4.16667 14.1667 4.53917 14.1667 5C14.1667 5.46083 14.5392 5.83333 15 5.83333H15.8333C16.2942 5.83333 16.6667 5.46083 16.6667 5ZM16.6667 8.33333C16.6667 7.8725 16.2942 7.5 15.8333 7.5H15C14.5392 7.5 14.1667 7.8725 14.1667 8.33333C14.1667 8.79417 14.5392 9.16667 15 9.16667H15.8333C16.2942 9.16667 16.6667 8.79417 16.6667 8.33333ZM20 15.8333V4.16667C20 1.86917 18.1308 0 15.8333 0H11.6667C9.36917 0 7.5 1.86917 7.5 4.16667C7.5 4.6275 7.8725 5 8.33333 5C8.79417 5 9.16667 4.6275 9.16667 4.16667C9.16667 2.78833 10.2883 1.66667 11.6667 1.66667H15.8333C17.2117 1.66667 18.3333 2.78833 18.3333 4.16667V15.8333C18.3333 17.2117 17.2117 18.3333 15.8333 18.3333H15C14.5392 18.3333 14.1667 18.7058 14.1667 19.1667C14.1667 19.6275 14.5392 20 15 20H15.8333C18.1308 20 20 18.1308 20 15.8333ZM13.3333 16.25V12.79C13.3333 11.5 12.7508 10.305 11.7342 9.50833L9.23417 7.55083C7.7225 6.36917 5.61083 6.36833 4.09917 7.55083L1.59917 9.5075C0.5825 10.3033 0 11.4992 0 12.7892V16.2492C0 18.3167 1.6825 19.9992 3.75 19.9992H9.58333C11.6508 19.9992 13.3333 18.3175 13.3333 16.25ZM8.2075 8.86417L10.7075 10.8208C11.3167 11.2983 11.6667 12.0167 11.6667 12.79V16.25C11.6667 17.3992 10.7325 18.3333 9.58333 18.3333H3.75C2.60083 18.3333 1.66667 17.3992 1.66667 16.25V12.79C1.66667 12.0158 2.01667 11.2983 2.62583 10.8208L5.12583 8.865C5.57917 8.51 6.1225 8.3325 6.66667 8.3325C7.21083 8.3325 7.75417 8.51 8.2075 8.86417ZM8.33333 15V13.3333C8.33333 12.8733 7.96 12.5 7.5 12.5H5.83333C5.37333 12.5 5 12.8733 5 13.3333V15C5 15.46 5.37333 15.8333 5.83333 15.8333H7.5C7.96 15.8333 8.33333 15.46 8.33333 15Z" />
                          </svg>
                        </i>
                        <Field
                          id="city"
                          placeholder="Enter city"
                          name="city"
                          size="10"
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className="inp_formCtrl">
                      <label>State</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M19.1665 6.63186V3.26246C19.6348 3.19395 19.9998 2.80713 19.9998 2.31839C19.9998 1.90066 19.7331 1.52972 19.3373 1.39688L15.3067 0.0501274C15.1075 -0.0167091 14.8926 -0.0167091 14.6942 0.0501274L10.6628 1.39772C10.267 1.52972 10.0003 1.90066 10.0003 2.31923C10.0003 2.80713 10.3653 3.19395 10.8336 3.26329V6.63269C10.3736 6.63269 10.0003 7.00698 10.0003 7.46815C10.0003 7.92932 10.3736 8.30361 10.8336 8.30361H19.1665C19.6273 8.30361 19.9998 7.92932 19.9998 7.46815C19.9998 7.00698 19.6273 6.63269 19.1665 6.63269V6.63186ZM17.4999 6.63186H15.8333V3.29003H17.4999V6.63186ZM12.5002 3.29003H14.1668V6.63186H12.5002V3.29003ZM10.0003 14.0156C10.0003 13.5979 9.73366 13.227 9.33785 13.0941L5.30639 11.7465C5.10723 11.6797 4.89224 11.6797 4.69392 11.7465L0.662465 13.0941C0.266653 13.2261 0 13.5971 0 14.0156C0 14.5035 0.364981 14.8903 0.83329 14.9597V18.3291C0.373314 18.3291 0 18.7025 0 19.1645C0 19.6266 0.373314 20 0.83329 20H9.16619C9.62616 20 9.99948 19.6266 9.99948 19.1645C9.99948 18.7025 9.62616 18.3291 9.16619 18.3291V14.9597C9.6345 14.8912 9.99948 14.5044 9.99948 14.0156H10.0003ZM2.5007 14.9864H4.16728V18.3283H2.5007V14.9864ZM5.83386 18.3283V14.9864H7.50044V18.3283H5.83386ZM19.7556 18.1553C20.0815 18.482 20.0815 19.01 19.7556 19.3366C19.5931 19.4996 19.3798 19.5814 19.1665 19.5814C18.9532 19.5814 18.7399 19.4996 18.5774 19.3366L16.7383 17.4928H13.3335C12.4144 17.4928 11.6669 16.7434 11.6669 15.8219V12.8644L6.4155 8.35206C6.04552 8.03459 5.83386 7.57174 5.83386 7.083V4.12549H2.84568C2.40071 4.12549 1.9824 3.95171 1.66741 3.63591L0.244987 2.20978C-0.0808291 1.88312 -0.0808291 1.35511 0.244987 1.02845C0.570804 0.701784 1.09744 0.701784 1.42326 1.02845L2.84568 2.45457H5.83386C6.75298 2.45457 7.50044 3.20398 7.50044 4.12549V7.083L12.751 11.5953C13.121 11.9136 13.3335 12.3765 13.3335 12.8644V15.8219H16.7383C17.1766 15.8219 17.6066 16.0007 17.9166 16.3115L19.7556 18.1553Z" />
                          </svg>
                        </i>
                        <Field
                          id="region"
                          placeholder="Enter state"
                          name="region"
                          size="10"
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className="inp_formCtrl">
                      <label>Postal Code</label>
                      <Box className="inp">
                        <i>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M6.54167 5.25C6.54167 4.813 6.89633 4.45833 7.33333 4.45833H13.6667C14.1045 4.45833 14.4583 4.813 14.4583 5.25C14.4583 5.687 14.1045 6.04167 13.6667 6.04167H7.33333C6.89633 6.04167 6.54167 5.687 6.54167 5.25ZM7.33333 9.20833H12.0833C12.5211 9.20833 12.875 8.85367 12.875 8.41667C12.875 7.97967 12.5211 7.625 12.0833 7.625H7.33333C6.89633 7.625 6.54167 7.97967 6.54167 8.41667C6.54167 8.85367 6.89633 9.20833 7.33333 9.20833ZM20 10.5605V15.5417C20 17.7243 18.2243 19.5 16.0417 19.5H4.95833C2.77571 19.5 1 17.7243 1 15.5417V10.5605C1 9.487 1.44492 8.44438 2.21996 7.70179L3.375 6.59504V3.66667C3.375 1.92025 4.79525 0.5 6.54167 0.5H14.4583C16.2048 0.5 17.625 1.92025 17.625 3.66667V6.59504L18.78 7.701C19.5551 8.44358 20 9.48542 20 10.5597V10.5605ZM4.95833 3.66667V10.1924L8.82008 14.0549C9.71783 14.9519 11.2822 14.9519 12.1791 14.0549L16.0417 10.1924V3.66667C16.0417 2.79346 15.3315 2.08333 14.4583 2.08333H6.54167C5.66846 2.08333 4.95833 2.79346 4.95833 3.66667ZM18.4167 10.5605C18.4167 10.4061 18.4008 10.2525 18.3708 10.1021L13.2985 15.1743C12.5512 15.9217 11.5577 16.3333 10.5 16.3333C9.44233 16.3333 8.44879 15.9217 7.70067 15.1743L2.62925 10.1013C2.59917 10.2517 2.58333 10.4053 2.58333 10.5597V15.5409C2.58333 16.8503 3.64892 17.9159 4.95833 17.9159H16.0417C17.3511 17.9159 18.4167 16.8503 18.4167 15.5409V10.5597V10.5605Z" />
                          </svg>
                        </i>
                        <Field
                          id="postal_code"
                          placeholder="Enter postal"
                          name="postal_code"
                          size="10"
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <ContainedButton
                      className="bbtn lc_Cirm"
                      title="Confirm"
                      type="submit"
                    />
                  </Grid>
                </Grid>
                <Box></Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
}

export default LocationDetails;
